import { useNavigate } from "react-router";
import { INTEGRATION_EVENT_LABEL, type IntegrationEvent } from "@metrickle/schema/constants";
import { authClient } from "../lib/auth";
import { usePageTitle } from "../lib/page-title";
import { ONBOARDING_TOPIC, WORKSPACE_TOPIC_LABEL, WORKSPACE_TOPICS, type NotificationChannel, type WorkspaceTopic } from "@metrickle/schema";
import { useNotificationPrefs, useSetNotificationPrefs } from "../lib/notifications";
import { usePushStatus } from "../lib/push";
import { PushSettings } from "../components/push-settings";
import { TwoFactorSettings } from "../components/two-factor";
import { guideStore, useGuidePrefs, useGuideState, useResetSeen, type GuideState } from "../lib/guides";
import { track, type Events } from "../lib/track";
import { RELEASES } from "../guides/releases";
import { TOURS, tourGuideId, type Tour } from "../guides/tours";
import { openWhatsNew, useResolvePath } from "../components/guides";
import { Button, Card, CardHeader, Disclosure, ErrorState, PageHeader, Skeleton, Switch, notifyError, toaster } from "../components/ui";

const fmtDay = (ms: number) => new Date(ms).toLocaleDateString(undefined, { year: "numeric", month: "short", day: "numeric" });

/** The signed-in person's own preferences. They follow them across workspaces and devices. */
export function Settings() {
  const session = authClient.useSession();
  const state = useGuideState();
  const prefs = useGuidePrefs();

  usePageTitle("Settings");

  const setPref = (patch: Partial<Pick<GuideState, "whatsNewEnabled" | "toursEnabled">>) => {
    const next = { whatsNewEnabled: state.data!.whatsNewEnabled, toursEnabled: state.data!.toursEnabled, ...patch };
    prefs.mutate(patch, {
      onSuccess: () => {
        track("guide_settings_changed", { whats_new: next.whatsNewEnabled, tours: next.toursEnabled });
        toaster.success({ title: "Saved" });
      },
      onError: notifyError,
    });
  };

  return (
    <>
      <PageHeader title="Settings" subtitle={session.data ? `Signed in as ${session.data.user.email}. These settings follow you across workspaces and devices.` : undefined} />
      {state.isError ? (
        <Card>
          <ErrorState title="Couldn't load your settings" error={state.error} />
        </Card>
      ) : !state.data ? (
        <Skeleton className="h-96" />
      ) : (
        <div className="max-w-3xl space-y-4">
          <TwoFactorSettings />

          <NotificationSettings />

          <Card>
            <CardHeader title="What's new" subtitle="A short walkthrough of what changed after we release something." />
            <div className="space-y-4 px-4 pb-4">
              <Switch
                hint="Off: you can still open it here, or from What's new in the sidebar."
                checked={state.data.whatsNewEnabled}
                readOnly={prefs.isPending}
                onCheckedChange={(v) => setPref({ whatsNewEnabled: v })}
              >
                Show what's new after an update
              </Switch>
              <div>
                <Button size="sm" onClick={() => openWhatsNew()}>
                  View what's new
                </Button>
              </div>
              <ReleaseHistory />
            </div>
          </Card>

          <Card>
            <CardHeader title="Tours" subtitle="A few steps pointing out what a screen shows and where to start." />
            <div className="space-y-4 px-4 pb-4">
              <Switch
                hint="Off: tours only start when you pick Replay below."
                checked={state.data.toursEnabled}
                readOnly={prefs.isPending}
                onCheckedChange={(v) => setPref({ toursEnabled: v })}
              >
                Show a tour the first time you open a screen
              </Switch>
              <TourList seen={state.data.seen} />
            </div>
          </Card>
        </div>
      )}
    </>
  );
}

const NOTIFICATION_GROUPS: { title: string; events: IntegrationEvent[] }[] = [
  { title: "Releases", events: ["alert.release_dropped", "release.verdict"] },
  { title: "Lost conversions", events: ["alert.task_success_drop", "alert.contract_broken", "alert.new_hotspot"] },
  { title: "Cases and fixes", events: ["case.opened", "case.closed", "issue.measured"] },
  { title: "Feedback and research", events: ["feedback.created", "study.booked", "study.completed"] },
];

const CHANNEL_LABEL: Record<NotificationChannel, string> = { inbox: "Inbox", email: "Email", push: "Push" };

/** Which events reach this person, and how, across every app they can see. */
function NotificationSettings() {
  const prefs = useNotificationPrefs();
  const set = useSetNotificationPrefs();
  const push = usePushStatus();
  // Push only appears once the server can send it.
  const pushOn = !!push.data?.enabled;
  const channels: NotificationChannel[] = pushOn ? ["inbox", "email", "push"] : ["inbox", "email"];
  const saved = (props: Events["notification_settings_changed"]) => ({
    onSuccess: () => {
      track("notification_settings_changed", props);
      toaster.success({ title: "Saved" });
    },
    onError: notifyError,
  });
  const toggle = (event: IntegrationEvent | WorkspaceTopic, channel: NotificationChannel, enabled: boolean) =>
    set.mutate({ changes: [{ event, channel, enabled }] }, saved({ event, channel, enabled }));

  return (
    <Card id="notifications" aria-busy={prefs.isPending}>
      <CardHeader
        title="Notifications"
        subtitle={`What reaches you, for every app you have access to: in your notifications inbox, by email${pushOn ? ", as a push notification on your phone or computer" : ""}, or more than one. Slack, Teams and webhooks are set per app under Integrations.`}
      />
      {prefs.isError ? (
        <ErrorState title="Couldn't load your notification settings" error={prefs.error} />
      ) : !prefs.data ? (
        <Skeleton className="m-4 h-48" />
      ) : (
        <div className="space-y-5 px-4 pb-4">
          <Switch
            hint="Monday morning: what's costing conversions across your apps, the top friction and fixes that worked. Not sent in a quiet week."
            checked={prefs.data.weeklyDigest}
            readOnly={set.isPending}
            onCheckedChange={(v) => set.mutate({ weeklyDigest: v }, saved({ event: "digest.weekly", channel: "email", enabled: v }))}
          >
            Weekly digest email
          </Switch>
          <Switch
            hint="A few emails in your first weeks: setting up, your first data, protecting a task and reading your first release verdict. Each one is sent once."
            checked={prefs.data.gettingStarted}
            readOnly={set.isPending}
            onCheckedChange={(v) => set.mutate({ gettingStarted: v }, saved({ event: ONBOARDING_TOPIC, channel: "email", enabled: v }))}
          >
            Getting started tips
          </Switch>
          {pushOn && <PushSettings status={push.data!} />}
          {NOTIFICATION_GROUPS.map((g) => (
            <fieldset key={g.title}>
              <legend className="mb-2 text-xs font-medium tracking-wide text-ink-3 uppercase">{g.title}</legend>
              <ul className="divide-y divide-line rounded-lg border border-line">
                {g.events.map((ev) => (
                  <TopicRow key={ev} {...INTEGRATION_EVENT_LABEL[ev]} channels={channels} on={prefs.data.events[ev]} busy={set.isPending} onChange={(ch, v) => toggle(ev, ch, v)} />
                ))}
              </ul>
            </fieldset>
          ))}
          {/* Only owners get workspace changes, so only they see these switches. */}
          {prefs.data.ownsWorkspace && (
            <fieldset>
              <legend className="mb-2 text-xs font-medium tracking-wide text-ink-3 uppercase">Workspaces you own</legend>
              <p className="mb-2 text-xs text-ink-3">Changes someone else makes to a workspace you own. You're never told about your own changes.</p>
              <ul className="divide-y divide-line rounded-lg border border-line">
                {WORKSPACE_TOPICS.map((t) => (
                  <TopicRow key={t} {...WORKSPACE_TOPIC_LABEL[t]} channels={channels} on={prefs.data.workspace[t]} busy={set.isPending} onChange={(ch, v) => toggle(t, ch, v)} />
                ))}
              </ul>
            </fieldset>
          )}
          <p className="text-xs text-ink-3">
            Task success drops, protected task gaps and release drops are emailed{pushOn ? " and pushed" : ""} by default: they cost conversions now.
            {prefs.data.ownsWorkspace && " Changes to workspaces you own are emailed by default too."} Every email has a link to stop that kind.
          </p>
        </div>
      )}
    </Card>
  );
}

function ReleaseHistory() {
  if (!RELEASES.length) return null;
  return (
    <Disclosure summary={`All updates (${RELEASES.length})`} className="rounded-lg border border-line px-3 py-2" contentClassName="-mx-3 border-t border-line px-3 pt-3">
      <ol className="space-y-3">
        {RELEASES.map((r) => (
          <li key={r.id}>
            <p className="text-xs text-ink-3">{fmtDay(Date.parse(`${r.date}T12:00:00Z`))}</p>
            <ul className="mt-1 list-disc space-y-1 pl-5 text-sm text-ink-2">
              {r.items.map((item) => (
                <li key={item.title}>
                  <span className="font-medium text-ink">{item.title}.</span> {item.body}
                </li>
              ))}
            </ul>
          </li>
        ))}
      </ol>
    </Disclosure>
  );
}

function TourList({ seen }: { seen: Record<string, number> }) {
  const reset = useResetSeen();
  const resolve = useResolvePath();
  const navigate = useNavigate();
  const anySeen = TOURS.some((t) => seen[tourGuideId(t)]);

  const replay = (tour: Tour, to: string) => {
    track("tour_replayed", { tour: tour.id });
    guideStore.queueTour(tour.id, window.location.pathname);
    navigate(to);
  };

  return (
    <>
      <ul className="divide-y divide-line rounded-lg border border-line">
        {TOURS.map((t) => {
          const at = seen[tourGuideId(t)];
          const to = resolve(t.route);
          const descId = `tour-${t.id}-desc`;
          return (
            <li key={t.id} className="flex flex-wrap items-center justify-between gap-x-4 gap-y-2 px-3 py-2.5">
              <div className="min-w-0">
                <p className="text-sm font-medium text-ink">{t.name}</p>
                <p id={descId} className="text-xs text-ink-3">
                  {t.description} {at ? `Seen ${fmtDay(at)}.` : "Not seen yet."}
                  {!to && " Add an app to take this tour."}
                </p>
              </div>
              <Button size="sm" disabled={!to} aria-describedby={descId} aria-label={`Replay the ${t.name} tour`} onClick={() => to && replay(t, to)}>
                Replay
              </Button>
            </li>
          );
        })}
      </ul>
      {anySeen && (
        <div>
          <Button
            size="sm"
            variant="ghost"
            pending={reset.isPending}
            pendingLabel="Resetting…"
            onClick={() =>
              reset.mutate("tour", {
                onSuccess: () => toaster.success({ title: "Tours reset", description: "Each tour starts again the next time you open its screen." }),
                onError: notifyError,
              })
            }
          >
            Reset all tours
          </Button>
        </div>
      )}
    </>
  );
}

/** One kind of notification, with a switch per channel. Each switch's name includes the kind, for screen readers. */
function TopicRow(p: { label: string; hint: string; channels: NotificationChannel[]; on: Record<NotificationChannel, boolean>; busy: boolean; onChange: (ch: NotificationChannel, v: boolean) => void }) {
  return (
    <li className="flex flex-wrap items-start justify-between gap-x-6 gap-y-2 px-3 py-2.5">
      <div className="min-w-0 flex-1 basis-60">
        <p className="text-sm font-medium text-ink">{p.label}</p>
        <p className="text-xs text-ink-3">{p.hint}</p>
      </div>
      <div className="flex flex-wrap gap-x-5 gap-y-2">
        {p.channels.map((ch) => (
          <Switch key={ch} checked={p.on[ch]} readOnly={p.busy} onCheckedChange={(v) => p.onChange(ch, v)}>
            {CHANNEL_LABEL[ch]}
            <span className="sr-only">: {p.label}</span>
          </Switch>
        ))}
      </div>
    </li>
  );
}
